import * as Sentry from "@sentry/react";
import { type PropsWithChildren, useState } from "react";
import { useTranslation } from "react-i18next";

import "react-pdf/dist/esm/Page/TextLayer.css";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LoadingSpinner } from "@/components/ui/spinner";

export const EditPdfConfirmationDialog = ({
  onSubmit,
  children,
}: PropsWithChildren<{
  onSubmit: () => Promise<void>;
}>) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      await onSubmit();
      setOpen(false);
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("split_pdf_dialog.title")}</DialogTitle>
          <DialogDescription>
            {t("split_pdf_dialog.description")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("split_pdf_dialog.cancel")}</Button>
          </DialogClose>
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={isLoading}
            className="gap-2"
          >
            {isLoading && <LoadingSpinner />}
            {t("split_pdf_dialog.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
